import type { RouteNamedMap } from 'vue-router/auto-routes';
import { normalizePath } from '@/shared/utils/stringUtils';

type RoutePath = keyof RouteNamedMap;

export type NavigationCategoryId =
  | 'projects'
  | 'asset-workflows'
  | 'spares-materials'
  | 'load-sheet'
  | 'reports'
  | 'logs-operations'
  | 'project-administration'
  | 'administration';

export interface NavRoute {
  name: RoutePath;
}

export interface NavigationVisibilityContext {
  hasSelectedProject: boolean;
  isSystemAdmin: boolean;
  isProjectViewer: boolean;
  /** True when the current user holds the ProjectOwner role in the selected project. Optional; defaults to false. */
  isProjectOwner?: boolean;
}

export interface NavigationCategory {
  id: NavigationCategoryId;
  label: string;
  icon: string;
  description: string;
}

export interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  description: string;
  category: NavigationCategoryId;
  route: NavRoute;
  keywords: readonly string[];
  requiresProject: boolean;
  requiresSystemAdmin: boolean;
  /** When true, only System Administrators or Project Owners can see this item. */
  requiresProjectOwnerOrAdmin?: boolean;
  projectViewerAccessible: boolean;
  standalone?: boolean;
  standalonePosition?: 'top' | 'bottom';
}

export interface NavigationGroup {
  category: NavigationCategory;
  items: NavigationItem[];
}

export const navigationCategories: readonly NavigationCategory[] = [
  {
    id: 'projects',
    label: 'Projects',
    icon: 'fa-folder-tree',
    description: 'Choose project context and manage projects.',
  },
  {
    id: 'asset-workflows',
    label: 'Asset Workflows',
    icon: 'fa-sitemap',
    description: 'Browse, search, and configure project assets.',
  },
  {
    id: 'spares-materials',
    label: 'Spares & Materials',
    icon: 'fa-boxes-stacked',
    description: 'Work with bills of materials, spare parts, and material records.',
  },
  {
    id: 'load-sheet',
    label: 'Load Sheet',
    icon: 'fa-file-import',
    description: 'Import, validate, and review shared load sheet data across workflows.',
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'fa-chart-column',
    description: 'Browse the reports catalog and open project reports.',
  },
  {
    id: 'logs-operations',
    label: 'Logs / Operations',
    icon: 'fa-clipboard-list',
    description: 'Inspect frontend application logs and operational activity.',
  },
  {
    id: 'project-administration',
    label: 'Project Administration',
    icon: 'fa-sliders',
    description: 'Manage settings and policies for the selected project.',
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: 'fa-shield-halved',
    description: 'System-wide management features for administrators.',
  },
] as const;

export const navigationItems: readonly NavigationItem[] = [
  {
    id: 'project-selection',
    label: 'Select a Project',
    icon: 'fa-folder-open',
    description: 'Pick the project context used by project-scoped pages.',
    category: 'projects',
    route: { name: '/home/project-selection' },
    keywords: ['project', 'selection', 'switch project', 'choose project'],
    requiresProject: false,
    requiresSystemAdmin: false,
    projectViewerAccessible: true,
  },
  {
    id: 'home',
    label: 'Dashboard',
    icon: 'fa-house',
    description: 'Open the selected project dashboard.',
    category: 'projects',
    route: { name: '/home/' },
    keywords: ['dashboard', 'home', 'overview', 'project dashboard', 'landing'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: true,
    standalone: true,
    standalonePosition: 'top',
  },
  {
    id: 'asset-hierarchy',
    label: 'Asset Hierarchy',
    icon: 'fa-sitemap',
    description: 'Explore and maintain the asset hierarchy.',
    category: 'asset-workflows',
    route: { name: '/asset-hierarchy/' },
    keywords: ['tree', 'structure', 'levels', 'hierarchy'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: true,
  },
  {
    id: 'asset-search',
    label: 'Asset Search',
    icon: 'fa-magnifying-glass',
    description: 'Find assets quickly across the selected project.',
    category: 'asset-workflows',
    route: { name: '/search-asset/' },
    keywords: ['find', 'lookup', 'search', 'assets'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: true,
  },
  {
    id: 'load-sheet',
    label: 'Load Sheet',
    icon: 'fa-file-import',
    description: 'Import, validate, and review load sheet data used across asset and materials workflows.',
    category: 'load-sheet',
    route: { name: '/load-sheet/' },
    keywords: ['import', 'spreadsheet', 'upload', 'load sheet', 'validation', 'materials'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: false,
  },
  {
    id: 'load-sheet-commits',
    label: 'Import History',
    icon: 'fa-clock-rotate-left',
    description: 'Browse the load sheet import timeline and open individual import details.',
    category: 'load-sheet',
    route: { name: '/load-sheet/commits/' },
    keywords: ['load sheet', 'import', 'history', 'timeline', 'review changes', 'diff'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: false,
  },
  {
    id: 'load-sheet-pending-reviews',
    label: 'Reviews',
    icon: 'fa-user-check',
    description: 'Review pending load sheet sessions and track review outcomes in one place.',
    category: 'load-sheet',
    route: { name: '/load-sheet/reviews/' },
    keywords: ['load sheet', 'reviews', 'pending review', 'approval', 'review results'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: false,
  },
  {
    id: 'validation-rules',
    label: 'Validation Rules',
    icon: 'fa-clipboard-check',
    description: 'Configure and manage field validation rules for entity data quality.',
    category: 'project-administration',
    route: { name: '/validation-rules/' },
    keywords: ['validation', 'rules', 'field', 'quality', 'regex', 'pattern', 'configuration'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: true,
  },
  {
    id: 'asset-support-types',
    label: 'Asset Support Types',
    icon: 'fa-tools',
    description: 'Manage asset support type definitions.',
    category: 'asset-workflows',
    route: { name: '/asset-support-types/' },
    keywords: ['support', 'types', 'asset support', 'maintenance'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: false,
  },
  {
    id: 'document-manager',
    label: 'Document Manager',
    icon: 'fa-file-circle-check',
    description: 'Manage documents and their asset associations.',
    category: 'asset-workflows',
    route: { name: '/document-manager/' },
    keywords: ['documents', 'document manager', 'files', 'manuals', 'association', 'upload'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: false,
  },
  {
    id: 'load-sheet-backups',
    label: 'Load Sheet Backups',
    icon: 'fa-database',
    description: 'Manage scheduled and manual Load Sheet data backup configurations and history.',
    category: 'load-sheet',
    route: { name: '/load-sheet-backups/' },
    keywords: ['backup', 'backups', 'load sheet backup', 'export', 'archive', 'schedule', 'history'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: false,
  },
  {
    id: 'spares-bom',
    label: 'Bill of Materials',
    icon: 'fa-boxes-stacked',
    description: 'Inspect and manage spare part bill of materials.',
    category: 'spares-materials',
    route: { name: '/spares/billofmaterials/' },
    keywords: ['spares', 'bill of materials', 'bom', 'inventory'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: true,
  },
  {
    id: 'spares-materials',
    label: 'Materials',
    icon: 'fa-box',
    description: 'Browse spare materials and related records.',
    category: 'spares-materials',
    route: { name: '/spares/materials' },
    keywords: ['spares', 'materials', 'inventory', 'parts'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: true,
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'fa-table-list',
    description: 'Open the reports landing page and browse available reports.',
    category: 'reports',
    route: { name: '/reports/' },
    keywords: ['reports', 'report catalog', 'available reports', 'landing page'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: false,
  },
  {
    id: 'reports-criticality',
    label: 'Criticality Report',
    icon: 'fa-triangle-exclamation',
    description: 'Open the criticality report for the selected project.',
    category: 'reports',
    route: { name: '/reports/criticality' },
    keywords: ['report', 'criticality', 'risk', 'priority', 'analytics'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: false,
  },
  {
    id: 'reports-integrity-check',
    label: 'Integrity Check',
    icon: 'fa-shield-halved',
    description: 'Scan asset data for referential integrity violations.',
    category: 'reports',
    route: { name: '/reports/integrity-check' },
    keywords: ['report', 'integrity', 'check', 'validation', 'soft-deleted', 'orphan'],
    requiresProject: true,
    requiresSystemAdmin: false,
    projectViewerAccessible: false,
  },
  {
    id: 'logs',
    label: 'Application Logs',
    icon: 'fa-clipboard-list',
    description: 'Review frontend application logs and trace client-side activity.',
    category: 'logs-operations',
    route: { name: '/logs' },
    keywords: ['frontend', 'client', 'activity', 'history', 'logs', 'operations'],
    requiresProject: false,
    requiresSystemAdmin: false,
    projectViewerAccessible: true,
    standalone: true,
    standalonePosition: 'bottom',
  },
  {
    id: 'project-settings',
    label: 'Project Settings',
    icon: 'fa-sliders',
    description: 'View and edit configuration settings for the selected project.',
    category: 'project-administration',
    route: { name: '/project-administration/project-settings' },
    keywords: ['project', 'settings', 'configuration', 'edit project', 'project config'],
    requiresProject: true,
    requiresSystemAdmin: false,
    requiresProjectOwnerOrAdmin: true,
    projectViewerAccessible: false,
  },
  {
    id: 'project-management',
    label: 'Project Management',
    icon: 'fa-project-diagram',
    description: 'Create and manage projects across the system.',
    category: 'administration',
    route: { name: '/project-management/' },
    keywords: ['admin', 'projects', 'management'],
    requiresProject: false,
    requiresSystemAdmin: true,
    projectViewerAccessible: false,
  },
  {
    id: 'user-management',
    label: 'User Management',
    icon: 'fa-users-cog',
    description: 'Administer users and account access.',
    category: 'administration',
    route: { name: '/user-management/' },
    keywords: ['admin', 'users', 'accounts', 'permissions'],
    requiresProject: false,
    requiresSystemAdmin: true,
    projectViewerAccessible: false,
  },
] as const;

const categoryById = new Map<NavigationCategoryId, NavigationCategory>(
  navigationCategories.map((category) => [category.id, category]),
);

const itemById = new Map<string, NavigationItem>(
  navigationItems.map((item) => [item.id, item]),
);

function getNormalizedRouteName(item: NavigationItem): string {
  return normalizePath(`${item.route.name}`);
}

function matchesPath(item: NavigationItem, path: string): boolean {
  // Strip query string and hash before comparing so paths like '/load-sheet/reviews?tab=pending'
  // correctly resolve to the '/load-sheet/reviews' nav item.
  const pathOnly = path.split('?')[0].split('#')[0];
  const normalizedCurrent = normalizePath(pathOnly);
  const normalizedTarget = getNormalizedRouteName(item);

  if (!normalizedCurrent || !normalizedTarget) {
    return false;
  }

  return (
    normalizedCurrent === normalizedTarget ||
    normalizedCurrent.startsWith(`${normalizedTarget}/`)
  );
}

function scoreSearchMatch(item: NavigationItem, category: NavigationCategory, query: string): number {
  const haystack = [
    item.label,
    item.description,
    category.label,
    category.description,
    ...item.keywords,
  ]
    .join(' ')
    .toLowerCase();

  if (!haystack.includes(query)) {
    return -1;
  }

  let score = 0;

  if (item.label.toLowerCase().includes(query)) {
    score += 4;
  }

  if (category.label.toLowerCase().includes(query)) {
    score += 2;
  }

  if (item.keywords.some((keyword) => keyword.toLowerCase().includes(query))) {
    score += 1;
  }

  return score;
}

export function getNavigationCategory(categoryId: NavigationCategoryId): NavigationCategory {
  const category = categoryById.get(categoryId);

  if (!category) {
    throw new Error(`Unknown navigation category: ${categoryId}`);
  }

  return category;
}

export function findNavigationItemById(itemId: string | null | undefined): NavigationItem | undefined {
  if (!itemId) {
    return undefined;
  }

  return itemById.get(itemId);
}

export function findNavigationItemByRouteName(routeName: string | symbol | null | undefined): NavigationItem | undefined {
  if (typeof routeName !== 'string') {
    return undefined;
  }

  const normalizedRouteName = normalizePath(routeName);

  return navigationItems.find((item) => getNormalizedRouteName(item) === normalizedRouteName);
}

export function findNavigationItemByPath(path: string): NavigationItem | undefined {
  const matches = navigationItems
    .filter((item) => matchesPath(item, path))
    .sort((left, right) => getNormalizedRouteName(right).length - getNormalizedRouteName(left).length);

  return matches[0];
}

export function isNavigationItemVisible(
  item: NavigationItem,
  context: NavigationVisibilityContext,
): boolean {
  if (item.id === 'project-selection' && context.hasSelectedProject && !context.isProjectViewer) {
    return false;
  }

  if (context.isProjectViewer && !item.projectViewerAccessible) {
    return false;
  }

  if (item.requiresSystemAdmin && !context.isSystemAdmin) {
    return false;
  }

  if (item.requiresProjectOwnerOrAdmin && !context.isSystemAdmin && !context.isProjectOwner) {
    return false;
  }

  if (item.requiresProject && !context.hasSelectedProject) {
    return false;
  }

  return true;
}

export function getVisibleNavigationItems(context: NavigationVisibilityContext): NavigationItem[] {
  return navigationItems.filter((item) => isNavigationItemVisible(item, context));
}

export function isNavigationPathAccessibleToProjectViewer(path: string): boolean {
  const navigationItem = findNavigationItemByPath(path);
  return navigationItem ? navigationItem.projectViewerAccessible : true;
}

export function isNavigationPathRequiresSystemAdmin(path: string): boolean {
  const navigationItem = findNavigationItemByPath(path);
  return navigationItem?.requiresSystemAdmin ?? false;
}

export function isNavigationPathRequiresProjectOwnerOrAdmin(path: string): boolean {
  const navigationItem = findNavigationItemByPath(path);
  return navigationItem?.requiresProjectOwnerOrAdmin ?? false;
}

export function getStandaloneNavigationItems(
  items: readonly NavigationItem[],
  position: 'top' | 'bottom' = 'top',
): NavigationItem[] {
  return items.filter(
    (item) => item.standalone === true && (item.standalonePosition ?? 'top') === position,
  );
}

export function groupNavigationItems(items: readonly NavigationItem[]): NavigationGroup[] {
  return navigationCategories
    .map((category) => ({
      category,
      items: items.filter((item) => item.standalone !== true && item.category === category.id),
    }))
    .filter((group) => group.items.length > 0);
}

export function searchNavigationItems(query: string, items: readonly NavigationItem[]): NavigationItem[] {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return [...items];
  }

  return [...items]
    .map((item) => ({
      item,
      score: scoreSearchMatch(item, getNavigationCategory(item.category), normalizedQuery),
    }))
    .filter((entry) => entry.score >= 0)
    .sort((left, right) => right.score - left.score)
    .map((entry) => entry.item);
}
